import {
  IAdhocActionResponse,
  IInvoiceAttachmentsResponse,
  IInvoiceExportQueuedResponse,
  IInvoiceListResponse,
  IInvoiceParams,
  IPaymentPlanInstalmentItem,
  IInvoiceResponse,
  IPaymentPlanTerms,
  ISavedCardChargeQuote,
  ICreatePaymentPromiseRequest,
  IPaymentPromise,
  IInvoiceQuery,
  IOpenInvoiceQueryRequest,
  IUpdateInvoiceQueryRequest,
  IResolveInvoiceQueryRequest,
} from 'shared';

import { CacheTags } from '../../types/cacheTags';
import { orgHeader } from '../../../../common/lib/axiosBaseQuery';
import { emptySplitApi } from '../baseApi';
import { buildNoteRequestBody } from '../noteFormData';

export const invoiceApi = emptySplitApi.injectEndpoints({
  endpoints: builder => ({
    fetchInvoices: builder.query<IInvoiceListResponse, IInvoiceParams & { organisationId: string }>({
      query: ({
        organisationId,
        page,
        sortColumn,
        sortType,
        invoiceTextSearch,
        xeroStatus,
        limit,
        frozen,
        groupId,
        contactId,
        reference,
        number,
        contactName,
        dueDateMaxDate,
        dueDateMinDate,
        issueDateMaxDate,
        issueDateMinDate,
        expectedPaymentDateMaxDate,
        expectedPaymentDateMinDate,
        dueAmount,
        dueAmountOperator,
        dueAmountMin,
        dueAmountMax,
        totalAmount,
        totalAmountOperator,
        totalAmountMin,
        totalAmountMax,
        isLateFee,
        daysOverdue,
        daysOverdueOperator,
        daysOverdueMin,
        daysOverdueMax,
        sentToCustomer,
        primaryContactName,
        currency,
        hasPaymentPlan,
        hasPaymentPromise,
        hasOpenQuery,
        invoiceTag,
      }) => ({
        url: `invoice`,
        method: `GET`,
        params: {
          page,
          sortColumn,
          sortType,
          invoiceTextSearch,
          xeroStatus,
          limit,
          frozen,
          groupId,
          contactId,
          reference,
          number,
          contactName,
          primaryContactName,
          dueDateMaxDate,
          dueDateMinDate,
          issueDateMaxDate,
          issueDateMinDate,
          expectedPaymentDateMaxDate,
          expectedPaymentDateMinDate,
          dueAmount,
          dueAmountOperator,
          dueAmountMin,
          dueAmountMax,
          totalAmount,
          totalAmountOperator,
          totalAmountMin,
          totalAmountMax,
          isLateFee,
          daysOverdue,
          daysOverdueOperator,
          daysOverdueMin,
          daysOverdueMax,
          sentToCustomer,
          currency,
          hasPaymentPlan,
          hasPaymentPromise,
          hasOpenQuery,
          invoiceTag,
        },
        headers: {
          [orgHeader]: organisationId,
        },
      }),
      keepUnusedDataFor: 1, // TEMP WORKAROUND FOR REPEATED QUERIES CACHE BREAKING DATAGRID
      providesTags: result => result?.invoices ?
        [
          ...result.invoices.map(({ invoice }) => ({ type: CacheTags.Invoices, id: invoice.id })),
          { type: CacheTags.Invoices, id: `PARTIAL-LIST` },
        ]
        : [{ type: CacheTags.Invoices, id: `PARTIAL-LIST` }],
    }),
    exportAllInvoices: builder.mutation<IInvoiceExportQueuedResponse, Partial<IInvoiceParams> & { organisationId?: string; filename?: string; columns?: string[] }>({
      query: ({ organisationId, ...params }) => ({
        url: `invoice/export`,
        method: `POST`,
        params: {
          ...params,
        },
        headers: {
          [orgHeader]: organisationId,
        },
      }),
      invalidatesTags: [{ type: CacheTags.ExportTasks, id: `LIST` }],
    }),
    fetchInvoice: builder.query<IInvoiceResponse, { organisationId: string; invoiceId: string}>({
      query: ({
        organisationId,
        invoiceId,
      }) => ({
        url: `invoice/${invoiceId}`,
        method: `GET`,
        headers: {
          [orgHeader]: organisationId,
        },
      }),
      providesTags: result => result &&
        [
          { type: CacheTags.Invoices, id: result.invoice?.id },
        ],
    }),
    fetchInvoiceAttachments: builder.query<IInvoiceAttachmentsResponse, { organisationId: string; invoiceId: string }>({
      query: ({
        organisationId,
        invoiceId,
      }) => ({
        url: `invoice/${invoiceId}/attachments`,
        method: `GET`,
        headers: {
          [orgHeader]: organisationId,
        },
      }),
    }),
    fetchSavedCards: builder.query<ISavedCardChargeQuote, { organisationId: string; invoiceId: string }>({
      query: ({
        organisationId,
        invoiceId,
      }) => ({
        url: `invoice/${invoiceId}/saved-cards`,
        method: `GET`,
        headers: {
          [orgHeader]: organisationId,
        },
      }),
      // Amount to charge follows the invoice
      providesTags: (_result, _error, { invoiceId }) => [{ type: CacheTags.Invoices, id: invoiceId }],
    }),
    paySavedCard: builder.mutation<{ ok: boolean; synced: boolean }, { organisationId: string; invoiceId: string; paymentMethodId: string }>({
      query: ({
        organisationId,
        invoiceId,
        paymentMethodId,
      }) => ({
        url: `invoice/${invoiceId}/pay-saved-card`,
        method: `POST`,
        headers: {
          [orgHeader]: organisationId,
        },
        data: {
          paymentMethodId,
        },
      }),
      invalidatesTags: (_result, _error, { invoiceId }) => [{ type: CacheTags.Invoices, id: invoiceId }],
    }),
    actionInvoice: builder.mutation({
      query: ({
        organisationId,
        invoiceId,
        action,
        penaltyId,
        discountId,
        actionId,
      }) => ({
        url: `invoice/${invoiceId}/action`,
        method: `POST`,
        headers: {
          [orgHeader]: organisationId,
        },
        data: {
          action,
          penaltyId,
          discountId,
          actionId,
        },
      }),
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      transformResponse: (response: any) => response?.invoice,
      invalidatesTags: ({ id }) => {
        return [{ type: CacheTags.Invoices, id }, { type: CacheTags.Actions, id: `LIST` }];
      },
    }),
    actionInvoices: builder.mutation({
      query: ({
        organisationId,
        invoiceIds,
        action,
      }) => ({
        url: `invoice/action/${action}`,
        method: `POST`,
        headers: {
          [orgHeader]: organisationId,
        },
        data: {
          invoiceIds,
        },
      }),
      invalidatesTags: ({ invoiceIds, action }) => {
        if (action === `sync-all`) {
          return [{ type: CacheTags.Organisations, id: `LIST` }];
        }

        return invoiceIds?.map(id => ({ type: CacheTags.Invoices, id }));
      },
    }),
    applyPolicyPreview: builder.mutation({
      query: ({
        organisationId,
        invoiceId,
        policyId,
      }) => ({
        url: `invoice/${invoiceId}/preview-policy/${policyId}/apply`,
        method: `POST`,
        headers: {
          [orgHeader]: organisationId,
        },
      }),
      invalidatesTags: ({ invoiceId }) => [{ type: CacheTags.Invoices, id: invoiceId }, { type: CacheTags.Actions, id: `LIST` }],
    }),
    fetchInvoicePromises: builder.query<IPaymentPromise[], { organisationId: string; invoiceId: string }>({
      query: ({ organisationId, invoiceId }) => ({
        url: `invoice/${invoiceId}/promises`,
        method: `GET`,
        headers: {
          [orgHeader]: organisationId,
        },
      }),
      providesTags: (_result, _error, { invoiceId }) => [{ type: CacheTags.PaymentPromises, id: invoiceId }],
    }),
    createInvoicePromise: builder.mutation<IPaymentPromise, { organisationId: string; invoiceId: string } & ICreatePaymentPromiseRequest>({
      query: ({ organisationId, invoiceId, ...data }) => ({
        url: `invoice/${invoiceId}/promises`,
        method: `POST`,
        headers: {
          [orgHeader]: organisationId,
        },
        data,
      }),
      invalidatesTags: (_result, _error, { invoiceId, contactCallId }) => [
        { type: CacheTags.PaymentPromises, id: invoiceId },
        { type: CacheTags.Invoices, id: invoiceId },
        { type: CacheTags.Actions, id: `LIST` },
        ...(contactCallId ? [{ type: CacheTags.CallFollowUps, id: contactCallId }] : []),
      ],
    }),
    cancelInvoicePromise: builder.mutation<void, { organisationId: string; invoiceId: string; promiseId: number }>({
      query: ({ organisationId, invoiceId, promiseId }) => ({
        url: `invoice/${invoiceId}/promises/${promiseId}/cancel`,
        method: `POST`,
        headers: {
          [orgHeader]: organisationId,
        },
      }),
      invalidatesTags: (_result, _error, { invoiceId }) => [
        { type: CacheTags.PaymentPromises, id: invoiceId },
        { type: CacheTags.Invoices, id: invoiceId },
        { type: CacheTags.Actions, id: `LIST` },
      ],
    }),
    fetchInvoiceQueries: builder.query<IInvoiceQuery[], { organisationId: string; invoiceId: string }>({
      query: ({ organisationId, invoiceId }) => ({
        url: `invoice/${invoiceId}/queries`,
        method: `GET`,
        headers: {
          [orgHeader]: organisationId,
        },
      }),
      providesTags: (_result, _error, { invoiceId }) => [{ type: CacheTags.InvoiceQueries, id: invoiceId }],
    }),
    openInvoiceQuery: builder.mutation<IInvoiceQuery, { organisationId: string; invoiceId: string } & IOpenInvoiceQueryRequest>({
      query: ({ organisationId, invoiceId, ...data }) => ({
        url: `invoice/${invoiceId}/queries`,
        method: `POST`,
        headers: {
          [orgHeader]: organisationId,
        },
        data,
      }),
      invalidatesTags: (_result, _error, { invoiceId, contactCallId }) => [
        { type: CacheTags.InvoiceQueries, id: invoiceId },
        { type: CacheTags.Invoices, id: invoiceId },
        { type: CacheTags.Actions, id: `LIST` },
        ...(contactCallId ? [{ type: CacheTags.CallFollowUps, id: contactCallId }] : []),
      ],
    }),
    updateInvoiceQuery: builder.mutation<IInvoiceQuery, { organisationId: string; invoiceId: string; queryId: string | number } & IUpdateInvoiceQueryRequest>({
      query: ({ organisationId, invoiceId, queryId, ...data }) => ({
        url: `invoice/${invoiceId}/queries/${queryId}`,
        method: `PATCH`,
        headers: {
          [orgHeader]: organisationId,
        },
        data,
      }),
      invalidatesTags: (_result, _error, { invoiceId }) => [
        { type: CacheTags.InvoiceQueries, id: invoiceId },
        { type: CacheTags.Invoices, id: invoiceId },
        { type: CacheTags.Actions, id: `LIST` },
      ],
    }),
    resolveInvoiceQuery: builder.mutation<IInvoiceQuery, { organisationId: string; invoiceId: string; queryId: string | number } & IResolveInvoiceQueryRequest>({
      query: ({ organisationId, invoiceId, queryId, ...data }) => ({
        url: `invoice/${invoiceId}/queries/${queryId}/resolve`,
        method: `POST`,
        headers: {
          [orgHeader]: organisationId,
        },
        data,
      }),
      invalidatesTags: (_result, _error, { invoiceId }) => [
        { type: CacheTags.InvoiceQueries, id: invoiceId },
        { type: CacheTags.Invoices, id: invoiceId },
        { type: CacheTags.Actions, id: `LIST` },
      ],
    }),
    assignInvoice: builder.mutation<void, { organisationId: string; invoiceId: string; userId: string | null }>({
      query: ({ organisationId, invoiceId, userId }) => ({
        url: `invoice/${invoiceId}/assign`,
        method: `PATCH`,
        headers: {
          [orgHeader]: organisationId,
        },
        data: { userId },
      }),
      invalidatesTags: (_result, _error, { invoiceId }) => [{ type: CacheTags.Invoices, id: invoiceId }],
    }),
    setExpectedPaymentDate: builder.mutation({
      query: ({
        organisationId,
        invoiceId,
        date,
      }) => ({
        url: `invoice/${invoiceId}/expected-payment-date`,
        method: `POST`,
        headers: {
          [orgHeader]: organisationId,
        },
        data: {
          date,
        },
      }),
      invalidatesTags: ({ invoiceId }) => [{ type: CacheTags.Invoices, id: invoiceId }, { type: CacheTags.Actions, id: `LIST` }],
    }),
    createNote: builder.mutation<unknown, { organisationId: string; invoiceId: string; content: string; mentionedUsers: string[]; attachments?: File[]; syncAttachments?: boolean }>({
      query: ({
        organisationId,
        invoiceId,
        content,
        mentionedUsers,
        attachments,
        syncAttachments,
      }) => {
        const body = buildNoteRequestBody({ content, mentionedUsers }, attachments, syncAttachments);

        return {
          url: `invoice/${invoiceId}/note`,
          method: `POST`,
          headers: {
            [orgHeader]: organisationId,
            ...body.headers,
          },
          data: body.data,
        };
      },
      invalidatesTags: ({ invoiceId }) => [{ type: CacheTags.Invoices, id: invoiceId }],
    }),
    uploadInvoicePdf: builder.mutation<{ emailedPdf: { fileName: string; receivedAt: string; fromAddress: string | null } }, { organisationId: string; invoiceId: string; file: File }>({
      query: ({ organisationId, invoiceId, file }) => {
        const formData = new FormData();

        formData.append(`file`, file);

        return {
          url: `invoice/${invoiceId}/pdf`,
          method: `POST`,
          headers: {
            [orgHeader]: organisationId,
          },
          data: formData,
        };
      },
      invalidatesTags: (_result, _error, { invoiceId }) => [{ type: CacheTags.Invoices, id: invoiceId }],
    }),
    adhocInvoiceEmail: builder.mutation<IAdhocActionResponse, { organisationId: string; invoiceId: string }>({
      query: ({ organisationId, invoiceId }) => ({
        url: `invoice/${invoiceId}/adhoc/reminder`,
        method: `POST`,
        headers: {
          [orgHeader]: organisationId,
        },
      }),
      invalidatesTags: [
        { type: CacheTags.Organisations, id: `LIST` },
        { type: CacheTags.Actions, id: `LIST` },
        { type: CacheTags.BadInvoices, id: `LIST` },
      ],
    }),
    adhocInvoiceSms: builder.mutation<IAdhocActionResponse, { organisationId: string; invoiceId: string }>({
      query: ({ organisationId, invoiceId }) => ({
        url: `invoice/${invoiceId}/adhoc/sms`,
        method: `POST`,
        headers: {
          [orgHeader]: organisationId,
        },
      }),
      invalidatesTags: [
        { type: CacheTags.Organisations, id: `LIST` },
        { type: CacheTags.Actions, id: `LIST` },
        { type: CacheTags.BadInvoices, id: `LIST` },
      ],
    }),
    adhocLateFee: builder.mutation<IAdhocActionResponse, { organisationId: string; invoiceId: string }>({
      query: ({ organisationId, invoiceId }) => ({
        url: `invoice/${invoiceId}/adhoc/latefee`,
        method: `POST`,
        headers: {
          [orgHeader]: organisationId,
        },
      }),
      invalidatesTags: [
        { type: CacheTags.Organisations, id: `LIST` },
        { type: CacheTags.Actions, id: `LIST` },
        { type: CacheTags.BadInvoices, id: `LIST` },
        { type: CacheTags.Invoices, id: `LIST` },
      ],
    }),
    createPaymentPlan: builder.mutation({
      query: ({
        organisationId,
        invoiceId,
        terms,
        adjustedInstalments,
        contactCallId,
      }: {
        organisationId: string;
        invoiceId: string;
        terms: IPaymentPlanTerms;
        adjustedInstalments?: IPaymentPlanInstalmentItem[];
        /** Recorded from a click to call's suggested follow ups */
        contactCallId?: string;
      }) => ({
        url: `invoice/${invoiceId}/payment-plan`,
        method: `POST`,
        headers: {
          [orgHeader]: organisationId,
        },
        data: {
          terms,
          adjustedInstalments,
          contactCallId,
        },
      }),
      invalidatesTags: (_result, _error, { invoiceId, contactCallId }) => [
        { type: CacheTags.Invoices, id: invoiceId },
        { type: CacheTags.PaymentPlans, id: `LIST` },
        ...(contactCallId ? [{ type: CacheTags.CallFollowUps, id: contactCallId }] : []),
      ],
    }),
    deletePaymentPlan: builder.mutation({
      query: ({
        organisationId,
        invoiceId,
        paymentPlanId,
      }) => ({
        url: `invoice/${invoiceId}/payment-plan/${paymentPlanId}`,
        method: `DELETE`,
        headers: {
          [orgHeader]: organisationId,
        },
      }),
      invalidatesTags: ({ invoiceId }) => [{ type: CacheTags.Invoices, id: invoiceId }, { type: CacheTags.PaymentPlans, id: `LIST` }],
    }),
  }),
});

export const {
  useFetchInvoiceQuery,
  useFetchInvoiceAttachmentsQuery,
  useFetchInvoicesQuery,
  useExportAllInvoicesMutation,
  useActionInvoiceMutation,
  useActionInvoicesMutation,
  useSetExpectedPaymentDateMutation,
  useFetchInvoicePromisesQuery,
  useCreateInvoicePromiseMutation,
  useCancelInvoicePromiseMutation,
  useFetchInvoiceQueriesQuery,
  useOpenInvoiceQueryMutation,
  useUpdateInvoiceQueryMutation,
  useResolveInvoiceQueryMutation,
  useAssignInvoiceMutation,
  useApplyPolicyPreviewMutation,
  useFetchSavedCardsQuery,
  usePaySavedCardMutation,
  useCreateNoteMutation,
  useUploadInvoicePdfMutation,
  useAdhocInvoiceEmailMutation,
  useAdhocLateFeeMutation,
  useAdhocInvoiceSmsMutation,
  useCreatePaymentPlanMutation,
  useDeletePaymentPlanMutation,
} = invoiceApi;
